import React, { FC } from 'react';
import styled from 'styled-components';
import {
  blockCardName,
  formatDuration,
  formatGap,
  formatSeconds,
  identityOf,
  Racer,
} from './model';
import { COLORS } from './theme';

/**
 * The card that opens under a racer's row: who they are, where they tapped,
 * what moved them and by how much, and how it ended.
 */

const Card = styled.div`
  margin: 2px 0 8px;
  padding: 9px 11px 10px;
  background: ${COLORS.cardBg};
  border: 1px solid ${COLORS.cardBorder};
  border-radius: 8px;
  font-size: 0.72rem;
  line-height: 1.45;
  color: ${COLORS.ink};
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const Glyph = styled.span`
  font-size: 1.05rem;
  line-height: 1;
`;

const Name = styled.span`
  flex: 1;
  min-width: 0;
  font-weight: 700;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Tag = styled.span<{ $tone: 'won' | 'muted' | 'early' }>`
  flex-shrink: 0;
  padding: 1px 8px;
  border-radius: 999px;
  font-size: 0.65rem;
  font-weight: 700;
  color: ${(p) => (p.$tone === 'won' ? COLORS.winnerInk : p.$tone === 'early' ? COLORS.earlyInk : COLORS.muted)};
  background: ${(p) => (p.$tone === 'won' ? COLORS.winnerWash : p.$tone === 'early' ? COLORS.earlyWash : '#f1f2f4')};
`;

const Receipt = styled.dl`
  display: grid;
  grid-template-columns: 1fr auto;
  column-gap: 0;
  row-gap: 2px;
  margin: 0;
  font-variant-numeric: tabular-nums;
`;

const Term = styled.dt<{ $strong?: boolean; $rule?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  color: ${(p) => (p.$strong ? COLORS.ink : COLORS.muted)};
  font-weight: ${(p) => (p.$strong ? 700 : 400)};
  ${(p) => (p.$rule ? `border-top: 1px solid ${COLORS.cardBorder}; padding-top: 3px; margin-top: 1px;` : '')}
`;

const Value = styled.dd<{ $strong?: boolean; $rule?: boolean }>`
  margin: 0;
  padding-left: 12px;
  text-align: right;
  white-space: nowrap;
  font-weight: ${(p) => (p.$strong ? 700 : 500)};
  ${(p) => (p.$rule ? `border-top: 1px solid ${COLORS.cardBorder}; padding-top: 3px; margin-top: 1px;` : '')}
`;

const Key = styled.span<{ $color: string }>`
  display: inline-block;
  width: 12px;
  height: 2px;
  border-radius: 1px;
  background: ${(p) => p.$color};
  flex-shrink: 0;
`;

// A div, not a p: the AOTD page styles every .art-of-the-day p as a big quote.
const Note = styled.div`
  margin: 6px 0 0;
  font-size: 0.68rem;
  font-style: italic;
  color: ${COLORS.muted};
`;

function tagFor(r: Racer): { text: string; tone: 'won' | 'muted' | 'early' } {
  if (r.isWinner) return { text: 'won', tone: 'won' };
  switch (r.kind) {
    case 'early':
      return { text: 'early', tone: 'early' };
    case 'blocked':
      return { text: 'blocked', tone: 'muted' };
    case 'late':
      return { text: 'late', tone: 'muted' };
    case 'hidden':
      return { text: 'time hidden', tone: 'muted' };
    default:
      return { text: 'lost', tone: 'muted' };
  }
}

interface Props {
  racer: Racer;
  runnerUp: Racer | null;
  marginMs: number | null;
  id: string;
}

const RacerDetail: FC<Props> = ({ racer: r, runnerUp, marginMs, id }) => {
  const tag = tagFor(r);
  const hasShifts = r.shifts.length > 0 && r.tappedMs !== null && r.countedMs !== null;

  const rows: React.ReactNode[] = [];
  if (r.kind === 'blocked') {
    if (r.countedMs !== null) {
      rows.push(
        <React.Fragment key="t">
          <Term>tapped at</Term>
          <Value>{formatSeconds(r.countedMs)}</Value>
        </React.Fragment>,
      );
    }
  } else if (hasShifts) {
    rows.push(
      <React.Fragment key="t">
        <Term>tapped at</Term>
        <Value>{formatSeconds(r.tappedMs as number)}</Value>
      </React.Fragment>,
    );
    r.shifts.forEach((s, i) => {
      rows.push(
        <React.Fragment key={`s${i}`}>
          <Term>
            <Key $color={s.deltaMs < 0 ? COLORS.headStart : COLORS.penalty} aria-hidden="true" />
            {s.label}
          </Term>
          <Value>{formatGap(s.deltaMs)}</Value>
        </React.Fragment>,
      );
    });
    rows.push(
      <React.Fragment key="c">
        <Term $strong $rule>
          counted as
        </Term>
        <Value $strong $rule>
          {formatSeconds(r.countedMs as number)}
        </Value>
      </React.Fragment>,
    );
  } else if (r.countedMs !== null) {
    rows.push(
      <React.Fragment key="t">
        <Term $strong>tapped at</Term>
        <Value $strong>{formatSeconds(r.countedMs)}</Value>
      </React.Fragment>,
    );
  }

  if (r.isWinner && marginMs !== null && runnerUp) {
    rows.push(
      <React.Fragment key="m">
        <Term>won by</Term>
        <Value>
          {formatDuration(marginMs)} over {identityOf(runnerUp)}
        </Value>
      </React.Fragment>,
    );
  } else if (r.gapMs !== null) {
    rows.push(
      <React.Fragment key="g">
        <Term>{r.gapMs >= 0 ? 'behind the winner' : 'ahead of the winner'}</Term>
        <Value>{formatDuration(r.gapMs)}</Value>
      </React.Fragment>,
    );
  }

  const notes: string[] = [];
  if (r.kind === 'early' && r.countedMs !== null) {
    notes.push(`Tapped ${formatDuration(r.countedMs)} before unlock, so that tap didn't count.`);
  }
  if (r.kind === 'early' && r.countedMs === null) notes.push("Tapped before unlock, so that tap didn't count.");
  if (r.kind === 'blocked') {
    const who = r.blockedBy.length > 0 ? `${r.blockedBy.join(', ')}'s ${blockCardName(r)}` : blockCardName(r);
    notes.push(`Blocked by ${who} before they could race.`);
  }
  if (r.kind === 'late') notes.push('Arrived after the day was already claimed, so it counted as a loss.');
  if (r.countedMs === null && r.kind !== 'blocked') notes.push("A card hid this racer's time.");
  const penalty = r.shifts.find((s) => s.isPenalty);
  if (penalty) {
    notes.push(
      `No emojis staged for their next AOTD, after already making one without: ${formatGap(penalty.deltaMs)}. Racing again the next day waives it.`,
    );
  }
  if (r.penaltyWaived) notes.push('No-emoji penalty waived: raced yesterday.');

  return (
    <Card id={id} role="region" aria-label={`${identityOf(r)} details`}>
      <Head>
        {r.emoji && <Glyph aria-hidden="true">{r.emoji}</Glyph>}
        <Name>{r.username}</Name>
        <Tag $tone={tag.tone}>{tag.text}</Tag>
      </Head>
      {rows.length > 0 && <Receipt>{rows}</Receipt>}
      {notes.map((n) => (
        <Note key={n}>{n}</Note>
      ))}
    </Card>
  );
};

export default RacerDetail;
